import { css } from "@emotion/react";
import { emitUserInteractionEvent } from "@/observability/tracking/avroEventTracking/emitUserInteractionEvent";
import { defaultCurrency } from "@zapier/currency";
import {
  EventAction,
  OptionSelected,
} from "@/observability/tracking/avroEventTracking/trackingTypes";
import {
  Button,
  Icon,
  IconName,
  Link,
  Text,
  Colors,
} from "@zapier/design-system";
import { Heading, useRendererContext } from "@zapier/builder.io-kit";
import { usePricingPageContext } from "@/pages/pricing";
import { getCustomResponsiveStyles } from "@/utils";
import { Price } from "../shared/Price";

export type CTACardProps = {
  stateKey: string;
  productID?: string;
  productPrice?: { monthlyPrice: number; annualPrice: number };
  icon?: IconName;
  title: {
    text?: string;
    tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  };
  description: string;
  priceDetails: string;
  primaryCTA: {
    text?: string;
    href?: string;
  };
  secondaryCTA: {
    text?: string;
    href?: string;
  };
};

const Styles = {
  root: css(
    {
      border: `1px solid ${Colors.earth}`,
      borderRadius: "5px",
      backgroundColor: Colors.neutral100,
      boxShadow: `5px 5px 0px 0px ${Colors.earth}`,
      display: "flex",
      height: "100%",
      width: "100%",
      overflow: "hidden",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        maxWidth: "570px",
      }),
    })
  ),
  margin: css({
    backgroundColor: Colors.lavender,
    width: "10px",
    flexShrink: "0",
  }),
  contentWrapper: css(
    {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      boxSizing: "border-box",
      padding: "15px",
      height: "100%",
      width: "100%",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        paddingLeft: "20px",
        paddingRight: "20px",
      }),
    })
  ),
  titleWrapper: css(
    {
      display: "flex",
      flexDirection: "column",
      marginBottom: "5px",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        flexDirection: "row",
        marginBottom: "10px",
      }),
    })
  ),
  iconWrapper: css(
    {
      display: "flex",
      alignItems: "center",
      marginBottom: "15px",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        marginRight: "10px",
        marginBottom: "0px",
      }),
    })
  ),
  description: css(
    {
      width: "93%",
      marginBottom: "35px",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        marginBottom: "20px",
      }),
    })
  ),
  priceLinksWrapper: css(
    ...getCustomResponsiveStyles({
      desktop: css({
        display: "flex",
        justifyContent: "space-between",
      }),
    })
  ),
  priceWrapper: css(
    {
      display: "flex",
      flexDirection: "column",
      marginBottom: "4px",
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        marginBottom: 0,
      }),
    })
  ),
  CTAWrapper: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    maxWidth: "280px",
  }),
  secondaryCTA: css(
    {
      color: Colors.earth,
      marginRight: "10px",
      fontWeight: 600,
      textUnderlineOffset: "3.3px",
      transition: "text-decoration-color 250ms",

      ":hover": {
        textDecorationColor: "transparent",
      },
    },
    ...getCustomResponsiveStyles({
      desktop: css({
        marginRight: "20px",
      }),
    })
  ),
  primaryCTAWrapper: css(
    {
      width: "173px",

      "a, button": {
        backgroundColor: `${Colors.earth} !important`,
      },
    },
    ...getCustomResponsiveStyles({
      desktop: css({ width: "148px" }),
    })
  ),
};

export default function CTACard(props: CTACardProps) {
  const { useStateByKey } = useRendererContext();
  const [{ isAnnual, selectedCurrency }] = useStateByKey(props.stateKey, {
    isAnnual: true,
    selectedCurrency: defaultCurrency,
  });

  const { products } = usePricingPageContext();

  const billingFrequency = isAnnual ? "annualPrice" : "monthlyPrice";

  const apiPrice =
    props.productID && products?.[props.productID]?.[billingFrequency];

  const hardCodedPrice = props.productPrice?.[billingFrequency];

  const price =
    typeof hardCodedPrice === "number" ? hardCodedPrice * 100 : apiPrice;

  const handlePrimaryClick = () => {
    emitUserInteractionEvent({
      destination_url_address: props.primaryCTA.href,
      event_action: EventAction.click,
      interaction_name: "cta_card_primary_cta_clicked",
      interaction_raw_text: props.primaryCTA.text,
      option_selected: OptionSelected.cta_button,
    });
  };

  const handleSecondaryClick = () => {
    emitUserInteractionEvent({
      destination_url_address: props.secondaryCTA.href,
      event_action: EventAction.click,
      interaction_name: "cta_card_secondary_cta_clicked",
      interaction_raw_text: props.secondaryCTA.text,
      option_selected: OptionSelected.cta_button,
    });
  };

  return (
    <div css={Styles.root}>
      <div css={Styles.margin} />
      <div css={Styles.contentWrapper}>
        <div>
          <div css={Styles.titleWrapper}>
            {props.icon && (
              <div css={Styles.iconWrapper}>
                <Icon name={props.icon} size="26px" color="earth" />
              </div>
            )}
            {props.title && props.title.text && (
              <Heading
                text={props.title.text}
                tag={props.title.tag}
                styles={{
                  desktop: "pageHeader9",
                  tablet: "pageHeader9",
                  mobile: "pageHeader9",
                }}
              />
            )}
          </div>
          <p css={Styles.description}>{props.description}</p>
        </div>
        <div css={Styles.priceLinksWrapper}>
          {props.productID && price !== null && (
            <div css={Styles.priceWrapper}>
              <Text type="paragraph3" fontWeight={700}>
                {props.priceDetails}
              </Text>
              {typeof price === "number" && (
                <Price currency={selectedCurrency} isAnnual={isAnnual}>
                  {price}
                </Price>
              )}
            </div>
          )}
          {(props.primaryCTA?.text || props.secondaryCTA?.text) && (
            <div css={Styles.CTAWrapper}>
              {props.secondaryCTA?.text && (
                <Link
                  href={props.secondaryCTA.href}
                  css={Styles.secondaryCTA}
                  onClick={handleSecondaryClick}
                >
                  {props.secondaryCTA.text}
                </Link>
              )}
              {props.primaryCTA?.text && (
                <div css={Styles.primaryCTAWrapper}>
                  <Button
                    isFullWidth={true}
                    href={props.primaryCTA.href}
                    onClick={handlePrimaryClick}
                    size="small"
                  >
                    {props.primaryCTA.text}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
